import { Map, List, fromJS } from "immutable";
import productionCapacityConstants from "../constants/productionCapacityConstants";

/**
 * Production Capacity Reducer
 *
 * Manages state for production capacity management feature including:
 * - Job financial calculations (price, discount, reseller fee, supplier fee, net revenue, gross profit)
 * - Filtering by company roles
 * - Period statistics
 * - Request states and modals
 *
 * DATA FLOW:
 * ==========
 * 1. API response arrives in actions/productionCapacityActions.js
 * 2. Response is transformed by dataTransformers.js to normalize field names
 * 3. Transformed data (with jobs, count, pages, page) is dispatched to this reducer
 * 4. Reducer stores data in immutable Map structure
 * 5. Components access via selectors and convert to JS objects at boundaries
 *
 * EXPECTED DATA STRUCTURE (from actions):
 * =======================================
 * action.data = {
 *   jobs: Array<TransformedJob>,  // Transformed job objects with flattened fields
 *   count: number,                 // Total number of records
 *   pages: number,                 // Total number of pages (calculated)
 *   page: number                   // Current page number
 * }
 */

export const initialState = Map({
	// Jobs list and pagination (aligned with subscription's list/meta structure)
	list: List([]),
	meta: Map({
		limit: 10,
		offset: 0,
		page: 1,
		total: 0,
	}),

	// Stats for the current period
	stats: Map({
		credits: 0,
		creditsCost: 0,
		consumption: 0,
		consumptionCost: 0,
		balance: 0,
		balanceCost: 0,
		backlog: 0,
	}),

	// Metrics chart data for 12-month overview
	metricsChart: Map({
		months: List([]),
		summary: Map({
			totalCredits: 0,
			totalCreditsCost: 0,
			totalCreditsCustomer: 0,
			totalConsumption: 0,
			totalConsumptionCost: 0,
			totalConsumptionCustomer: 0,
			totalBalance: 0,
			totalBalanceCost: 0,
			totalBalanceCustomer: 0,
			totalBacklog: 0,
		}),
	}),

	// Filter state (aligned with subscription pattern)
	filters: Map({
		search: "",
		companySelection: List([]),
		companyRoles: List([]), // For filtering by customer/reseller/supplier
		resellerCompanyId: List([]),
		supplierCompanyId: List([]),
		customerCompanyId: List([]),
		serviceTypeId: List([]),
		status: List([]),
		fromDate: List([]),
		toDate: List([]),
		limit: 10,
		page: 1,
	}),

	// Query parameters (aligned with subscription pattern)
	queryParameter: Map({
		page: 1,
		search: "",
		companySelection: List([]),
		companyRoles: List([]),
		resellerCompanyId: List([]),
		supplierCompanyId: List([]),
		customerCompanyId: List([]),
		serviceTypeId: List([]),
		status: List([]),
		fromDate: List([]),
		toDate: List([]),
		limit: 10,
	}),

	// Loading states (aligned with subscription pattern)
	loading: Map({
		list: false,
		stats: false,
		update: false,
		metricsChart: false,
	}),

	// Current job for details view
	current: Map(),

	// Request state (aligned with subscription pattern)
	request: false,
	action: false,

	// Success/error messages (aligned with subscription pattern)
	successMessage: "",
	errorMessage: "",
	requestMessage: "",
	error: null,
});

const productionCapacityReducer = (state = initialState, action) => {
	switch (action.type) {
		// List request/success/failure (aligned with subscription pattern)
		case productionCapacityConstants.PRODUCTION_CAPACITY_LIST_REQUEST:
			return state.setIn(["loading", "list"], true).set("error", null);

		case productionCapacityConstants.GET_PRODUCTION_CAPACITY_JOBS:
			return state
				.set("list", fromJS(action.data?.jobs || action.data?.list || []))
				.set(
					"meta",
					fromJS(
						action.data?.meta || {
							limit: action.data?.limit || 10,
							offset: action.data?.offset || 0,
							page: action.data?.page || 1,
							total: action.data?.count || action.data?.total || 0,
						}
					)
				)
				.setIn(["loading", "list"], false)
				.set("error", null);

		case productionCapacityConstants.PRODUCTION_CAPACITY_LIST_FAILURE:
			return state
				.setIn(["loading", "list"], false)
				.set("error", action.error || null);

		// Stats request/success/failure (aligned with subscription pattern)
		case productionCapacityConstants.PRODUCTION_CAPACITY_STATS_REQUEST:
			return state.setIn(["loading", "stats"], true).set("error", null);

		case productionCapacityConstants.GET_PRODUCTION_CAPACITY_STATS:
			return state
				.set("stats", fromJS(action.data || {}))
				.setIn(["loading", "stats"], false)
				.set("error", null);

		case productionCapacityConstants.PRODUCTION_CAPACITY_STATS_FAILURE:
			return state
				.setIn(["loading", "stats"], false)
				.set("error", action.error || null);

		// Metrics Chart request/success/failure
		case productionCapacityConstants.METRICS_CHART_REQUEST:
			return state.setIn(["loading", "metricsChart"], true).set("error", null);

		case productionCapacityConstants.GET_METRICS_CHART:
			return state
				.setIn(["metricsChart", "months"], fromJS(action.data?.months || []))
				.setIn(["metricsChart", "summary"], fromJS(action.data?.summary || {}))
				.setIn(["loading", "metricsChart"], false)
				.set("error", null);

		case productionCapacityConstants.METRICS_CHART_FAILURE:
			return state
				.setIn(["loading", "metricsChart"], false)
				.set("error", action.error || null);

		// Filters update (aligned with subscription pattern)
		case productionCapacityConstants.SET_PRODUCTION_CAPACITY_FILTERS:
			return state.mergeIn(
				["filters"],
				fromJS(action.data || action.payload || {})
			);

		case productionCapacityConstants.CLEAR_PRODUCTION_CAPACITY_FILTERS:
			return state.set("filters", initialState.get("filters"));

		// Query parameters (aligned with subscription pattern)
		case productionCapacityConstants.SET_PRODUCTION_CAPACITY_QUERY_PARAMETER:
			return state.mergeIn(["queryParameter"], fromJS(action.data || {}));

		case productionCapacityConstants.CLEAR_PRODUCTION_CAPACITY_QUERY_PARAMETER:
			return state.set("queryParameter", initialState.get("queryParameter"));

		// Current job (aligned with subscription's current pattern)
		case productionCapacityConstants.SET_CURRENT_PRODUCTION_CAPACITY_JOB:
			return state
				.set("current", fromJS(action.payload || action.data || {}))
				.set("error", null);

		case productionCapacityConstants.CLEAR_CURRENT_PRODUCTION_CAPACITY_JOB:
			return state.set("current", Map());

		// Update actions (aligned with subscription pattern)
		case productionCapacityConstants.PRODUCTION_CAPACITY_UPDATE_REQUEST:
			return state
				.setIn(["loading", "update"], true)
				.set("error", null)
				.set("action", false)
				.set("request", false)
				.set("requestMessage", "")
				.set("successMessage", "")
				.set("errorMessage", "");

		case productionCapacityConstants.PRODUCTION_CAPACITY_UPDATE_SUCCESS:
			return state
				.setIn(["loading", "update"], false)
				.set("error", null)
				.set("action", true)
				.set("request", true)
				.set("requestMessage", "Updated successfully")
				.set("successMessage", "Updated successfully")
				.set("errorMessage", "");

		case productionCapacityConstants.PRODUCTION_CAPACITY_UPDATE_FAILURE:
			return state
				.setIn(["loading", "update"], false)
				.set("action", false)
				.set("request", true)
				.set("requestMessage", "Failed to update")
				.set("errorMessage", "Failed to update")
				.set("error", action.error || null);

		// Action success/failure (aligned with subscription pattern)
		case productionCapacityConstants.PC_ACTION_SUCCESS:
			return state
				.set("action", true)
				.set("request", true)
				.set("successMessage", action.message || action.data || "")
				.set("requestMessage", action.message || action.data || "")
				.set("errorMessage", "")
				.set("error", null);

		case productionCapacityConstants.PC_ACTION_FAILURE:
			return state
				.set("action", false)
				.set("request", true)
				.set("requestMessage", action.error || "")
				.set("errorMessage", action.error || "")
				.set("error", action.error || null);

		// Request state management (aligned with subscription pattern)
		case productionCapacityConstants.PC_REQUEST:
			return state
				.set("action", action.data?.success || false)
				.set("request", true)
				.set("requestMessage", action.data?.message || "");

		case productionCapacityConstants.PC_REQUEST_DONE:
			return state
				.set("action", false)
				.set("request", false)
				.set("error", null)
				.set("successMessage", "")
				.set("errorMessage", "")
				.set("requestMessage", "");

		case productionCapacityConstants.PRODUCTION_CAPACITY_CLEAR_REQUEST_STATE:
			return state
				.set("action", false)
				.set("request", false)
				.set("requestMessage", "")
				.set("successMessage", "")
				.set("errorMessage", "")
				.set("error", null);

		// Clear all
		case productionCapacityConstants.CLEAR_PRODUCTION_CAPACITY:
			return initialState;

		default:
			return state;
	}
};

export default productionCapacityReducer;
